import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  Chip,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import QuotationService from "../services/QuotationService";
import LeadService from "../services/LeadService";
import CatalogProductPickerDialog from "../components/CatalogProductPickerDialog";
import { PAGE_BG, PURPLE, PURPLE_DARK } from "../components/leadConstants";
import {
  computeQuotationTotals,
  formatMoney,
  formatQuotationLabel,
  minorToAmount,
  normalizeLineItem,
  todayDateInputValue,
} from "../utils/quotationUtils";
import {
  deliveryAddressFromQuotation,
  EMPTY_DELIVERY_ADDRESS,
  normalizeDeliveryAddress,
} from "../utils/quotationAddressUtils";

const INK = "#0f172a";
const MUTED = "#64748b";
const LINE = "#e8edf3";

const emptyCharge = () => ({ title: "", type: "fixed", value: "" });
const emptyTax = () => ({ title: "", rate_percent: "" });

function getInitials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.[0] || "?").toUpperCase();
}

function SectionCard({ title, subtitle, action, children, noPadding = false }) {
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2.5,
        border: `1px solid ${LINE}`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#fff",
        boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
        maxWidth: "100%",
      }}
    >
      <Box sx={{ height: 3, background: `linear-gradient(90deg, ${PURPLE} 0%, ${PURPLE_DARK} 100%)` }} />
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: `1px solid ${LINE}`,
          bgcolor: "#fafbfc",
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color={INK}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color={MUTED}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      <Box sx={noPadding ? undefined : { p: 2.5 }}>{children}</Box>
    </Paper>
  );
}

function SummaryLine({ label, value, strong = false, negative = false }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
      <Typography variant="body2" color={strong ? INK : MUTED} fontWeight={strong ? 700 : 500}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={strong ? 700 : 600}
        color={negative ? "error.main" : strong ? PURPLE_DARK : INK}
      >
        {value}
      </Typography>
    </Box>
  );
}

function ProductThumb({ imageUrl, name }) {
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "#f1f5f9",
        border: `1px solid ${LINE}`,
        flexShrink: 0,
      }}
    >
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={name}
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: MUTED,
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {getInitials(name)}
        </Box>
      )}
    </Box>
  );
}

function CompactNumberField({ value, onChange, width = 72, ...props }) {
  return (
    <TextField
      type="number"
      size="small"
      value={value}
      onChange={onChange}
      sx={{
        width,
        "& .MuiOutlinedInput-root": {
          bgcolor: "#fff",
          fontSize: "0.8125rem",
          "& fieldset": { borderColor: LINE },
        },
        "& .MuiOutlinedInput-input": { py: 0.75, textAlign: "right" },
      }}
      {...props}
    />
  );
}

const adjustmentFieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#fff",
    fontSize: "0.8125rem",
  },
};

function AdjustmentBox({ children, sx }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${LINE}`,
        bgcolor: "#fafbfc",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function AdjustmentRowHeader({ title, actionLabel, onAction }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
      <Typography variant="body2" fontWeight={600} color={INK}>
        {title}
      </Typography>
      <Button
        size="small"
        onClick={onAction}
        sx={{ textTransform: "none", fontSize: "0.75rem", minWidth: 0, px: 0.5 }}
      >
        {actionLabel}
      </Button>
    </Box>
  );
}

function DeleteRowButton({ onClick }) {
  return (
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        width: 36,
        height: 36,
        color: MUTED,
        "&:hover": { color: "error.main", bgcolor: "error.50" },
      }}
    >
      <DeleteOutlineIcon fontSize="small" />
    </IconButton>
  );
}

export default function QuotationFormView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedLeadId = searchParams.get("leadId") || "";
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [previewNumber, setPreviewNumber] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [form, setForm] = useState({
    lead_id: "",
    contact_id: "",
    quotation_date: todayDateInputValue(),
    valid_until: "",
    notes: "",
    currency: "INR",
    delivery_address: { ...EMPTY_DELIVERY_ADDRESS },
    line_items: [],
    adjustments: {
      discount: null,
      charges: [],
      taxes: [],
    },
  });

  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountType, setDiscountType] = useState("fixed");
  const [discountValue, setDiscountValue] = useState("");

  const totals = useMemo(() => {
    const adjustments = {
      discount: discountEnabled
        ? {
          type: discountType,
          value: discountValue,
          value_minor: discountType === "fixed" ? Math.round(parseFloat(discountValue || 0) * 100) : undefined,
        }
        : null,
      charges: form.adjustments.charges,
      taxes: form.adjustments.taxes,
    };
    return computeQuotationTotals(form.line_items, adjustments);
  }, [form.line_items, form.adjustments.charges, form.adjustments.taxes, discountEnabled, discountType, discountValue]);

  const selectedLead = useMemo(
    () => leads.find((item) => item.id === form.lead_id),
    [leads, form.lead_id],
  );

  const loadLeads = useCallback(async () => {
    try {
      const data = await LeadService.getLeads({ limit: 200 });
      setLeads(data.leads || []);
    } catch {
      setLeads([]);
    }
  }, []);

  const loadQuotation = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const data = await QuotationService.getQuotationById(id);
      setForm({
        lead_id: data.lead_id || "",
        contact_id: data.contact_id || "",
        quotation_date: data.quotation_date?.slice?.(0, 10) || data.quotation_date,
        valid_until: data.valid_until?.slice?.(0, 10) || data.valid_until || "",
        notes: data.notes || "",
        currency: data.currency || "INR",
        delivery_address: deliveryAddressFromQuotation(data),
        line_items: data.line_items || [],
        adjustments: data.adjustments || { discount: null, charges: [], taxes: [] },
      });
      const discount = data.adjustments?.discount;
      if (discount) {
        setDiscountEnabled(true);
        setDiscountType(discount.type || "fixed");
        setDiscountValue(
          discount.type === "fixed"
            ? String(minorToAmount(data.discount_minor))
            : String(discount.value || ""),
        );
      }
      setPreviewNumber(data.quotation_number);
    } catch {
      setSnackbar({ open: true, message: "Failed to load quotation", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    loadLeads();
    if (isEdit) loadQuotation();
    else {
      QuotationService.getNextNumber()
        .then((data) => setPreviewNumber(data.quotation_number))
        .catch(() => setPreviewNumber(1));
    }
  }, [isEdit, loadLeads, loadQuotation]);

  useEffect(() => {
    if (isEdit || !preselectedLeadId || !leads.length) return;
    const lead = leads.find((item) => item.id === preselectedLeadId);
    if (!lead) return;
    setForm((prev) => {
      if (prev.lead_id) return prev;
      return {
        ...prev,
        lead_id: lead.id,
        contact_id: lead.contact_id || "",
      };
    });
  }, [isEdit, preselectedLeadId, leads]);

  const handleLeadChange = (leadId) => {
    const lead = leads.find((item) => item.id === leadId);
    setForm((prev) => ({
      ...prev,
      lead_id: leadId,
      contact_id: lead?.contact_id || "",
      delivery_address: normalizeDeliveryAddress({
        ...prev.delivery_address,
        name: lead?.contact_name || prev.delivery_address?.name,
        phone: lead?.contact_phone || prev.delivery_address?.phone,
      }),
    }));
  };

  const handleProductsConfirm = (items) => {
    setForm((prev) => {
      const existing = new Map(
        prev.line_items.map((item) => [item.retailer_id || item.catalog_product_id, item]),
      );
      items.forEach((item) => {
        const key = item.retailer_id || item.catalog_product_id;
        if (!existing.has(key)) existing.set(key, item);
      });
      return { ...prev, line_items: Array.from(existing.values()) };
    });
  };

  const updateLineItem = (index, patch) => {
    setForm((prev) => {
      const nextItems = prev.line_items.map((item, i) => {
        if (i !== index) return item;
        return normalizeLineItem({ ...item, ...patch }, index);
      });
      return { ...prev, line_items: nextItems };
    });
  };

  const removeLineItem = (index) => {
    setForm((prev) => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }));
  };

  const updateCharge = (index, patch) => {
    setForm((prev) => {
      const charges = [...(prev.adjustments.charges || [])];
      charges[index] = { ...charges[index], ...patch };
      return { ...prev, adjustments: { ...prev.adjustments, charges } };
    });
  };

  const updateTax = (index, patch) => {
    setForm((prev) => {
      const taxes = [...(prev.adjustments.taxes || [])];
      taxes[index] = { ...taxes[index], ...patch };
      return { ...prev, adjustments: { ...prev.adjustments, taxes } };
    });
  };

  const validate = () => {
    if (!form.lead_id || !form.contact_id) return "Lead is required";
    if (!form.quotation_date) return "Date is required";
    if (!form.line_items.length) return "Add at least one product";
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      setSnackbar({ open: true, message: error, severity: "error" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        lead_id: form.lead_id,
        contact_id: form.contact_id,
        quotation_date: form.quotation_date,
        valid_until: form.valid_until || null,
        notes: form.notes || null,
        currency: form.currency,
        delivery_address: normalizeDeliveryAddress(form.delivery_address),
        line_items: totals.lineItems,
        adjustments: totals.adjustments,
      };

      if (isEdit) {
        await QuotationService.updateQuotation(id, payload);
        setSnackbar({ open: true, message: "Quotation updated", severity: "success" });
      } else {
        await QuotationService.createQuotation(payload);
        setSnackbar({ open: true, message: "Quotation created", severity: "success" });
      }
      navigate("/quotations");
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || "Failed to save quotation",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const thSx = {
    fontWeight: 600,
    color: MUTED,
    fontSize: "0.7rem",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${LINE}`,
    py: 1,
    px: 2,
    bgcolor: "#f8fafc",
  };

  const tdSx = {
    borderBottom: `1px solid ${LINE}`,
    py: 1.25,
    px: 2,
    verticalAlign: "middle",
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", bgcolor: PAGE_BG }}>
        <CircularProgress sx={{ color: PURPLE }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: PAGE_BG, pb: 4 }}>
      {/* Top bar */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "#fff",
          borderBottom: `1px solid ${LINE}`,
          px: { xs: 2, md: 3 },
          py: 1.5,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: "auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={() => navigate("/quotations")} size="small" sx={{ color: MUTED }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" fontWeight={700} color={INK} lineHeight={1.2}>
                {isEdit ? "Edit Quotation" : "Create Quotation"}
              </Typography>
              {previewNumber && (
                <Typography variant="caption" color={MUTED}>
                  {formatQuotationLabel(previewNumber)}
                </Typography>
              )}
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => navigate("/quotations")}
              sx={{ textTransform: "none", color: MUTED }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: PURPLE,
                "&:hover": { bgcolor: PURPLE_DARK },
                minWidth: 140,
              }}
            >
              {saving ? "Saving…" : isEdit ? "Update Quotation" : "Create Quotation"}
            </Button>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 3 }, pt: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 320px" },
            gap: 2.5,
            alignItems: "start",
          }}
        >
          {/* Main form */}
          <Box sx={{ minWidth: 0 }}>
            <SectionCard title="Quotation Details" subtitle="Basic information for this quote">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Quotation ID"
                    value={previewNumber ? formatQuotationLabel(previewNumber) : ""}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Date"
                    type="date"
                    value={form.quotation_date}
                    onChange={(e) => setForm((prev) => ({ ...prev, quotation_date: e.target.value }))}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Valid until"
                    type="date"
                    value={form.valid_until}
                    onChange={(e) => setForm((prev) => ({ ...prev, valid_until: e.target.value }))}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small" error={!form.lead_id}>
                    <InputLabel>Lead / Customer</InputLabel>
                    <Select
                      label="Lead / Customer"
                      value={form.lead_id}
                      onChange={(e) => handleLeadChange(e.target.value)}
                    >
                      {leads.map((lead) => (
                        <MenuItem key={lead.id} value={lead.id}>
                          {lead.title}
                          {lead.contact_name ? ` — ${lead.contact_name}` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!form.lead_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                      Lead is required
                    </Typography>
                  )}
                  {selectedLead && (
                    <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {selectedLead.contact_name && (
                        <Chip size="small" label={selectedLead.contact_name} variant="outlined" />
                      )}
                      {selectedLead.contact_phone && (
                        <Chip size="small" label={selectedLead.contact_phone} variant="outlined" />
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard
              title="Products"
              subtitle={`${form.line_items.length} item${form.line_items.length === 1 ? "" : "s"} in this quotation`}
              noPadding
              action={(
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setPickerOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: PURPLE,
                    "&:hover": { bgcolor: PURPLE_DARK },
                  }}
                >
                  Add Products
                </Button>
              )}
            >
              {form.line_items.length === 0 ? (
                <Box
                  sx={{
                    py: 5,
                    px: 3,
                    textAlign: "center",
                    borderTop: `1px dashed ${LINE}`,
                  }}
                >
                  <Inventory2OutlinedIcon sx={{ fontSize: 40, color: MUTED, mb: 1 }} />
                  <Typography variant="body1" fontWeight={600} color={INK} gutterBottom>
                    No products added yet
                  </Typography>
                  <Typography variant="body2" color={MUTED} sx={{ mb: 2 }}>
                    Search your catalog and add items to build this quotation.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setPickerOpen(true)}
                    sx={{ textTransform: "none", borderColor: PURPLE, color: PURPLE }}
                  >
                    Search & Add Products
                  </Button>
                </Box>
              ) : (
                <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                  <Table size="small" sx={{ minWidth: 560, width: "100%" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={thSx}>Product</TableCell>
                        <TableCell align="center" sx={thSx} width={90}>Qty</TableCell>
                        <TableCell align="right" sx={thSx} width={110}>Unit Price</TableCell>
                        <TableCell align="center" sx={thSx} width={90}>GST %</TableCell>
                        <TableCell align="right" sx={thSx} width={110}>Total</TableCell>
                        <TableCell align="center" sx={thSx} width={48} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {form.line_items.map((item, index) => (
                        <TableRow
                          key={item.retailer_id || item.catalog_product_id || index}
                          sx={{ "&:hover": { bgcolor: "#fafbfc" }, "&:last-child td": { borderBottom: 0 } }}
                        >
                          <TableCell sx={tdSx}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                              <ProductThumb imageUrl={item.image_url} name={item.name} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={700} color={INK} noWrap>
                                  {item.name}
                                </Typography>
                                {item.description && (
                                  <Typography variant="caption" color={MUTED} display="block" noWrap>
                                    {item.description}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={tdSx}>
                            <CompactNumberField
                              value={item.quantity}
                              onChange={(e) => updateLineItem(index, { quantity: e.target.value })}
                              inputProps={{ min: 1 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={tdSx}>
                            <CompactNumberField
                              width={100}
                              value={minorToAmount(item.unit_price_minor)}
                              onChange={(e) => updateLineItem(index, { unit_price: e.target.value })}
                              inputProps={{ min: 0, step: "0.01" }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={tdSx}>
                            <CompactNumberField
                              value={item.tax_rate ?? ""}
                              onChange={(e) => updateLineItem(index, { tax_rate: e.target.value })}
                              inputProps={{ min: 0, step: "0.01" }}
                              placeholder="0"
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ ...tdSx, fontWeight: 700, color: INK }}>
                            {formatMoney(item.line_total_minor + (item.tax_amount_minor || 0))}
                          </TableCell>
                          <TableCell align="center" sx={tdSx}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeLineItem(index)}
                              sx={{ opacity: 0.7, "&:hover": { opacity: 1 } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </SectionCard>

            <SectionCard
              title="Delivery address"
              subtitle="Used when converting this quotation to an order"
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Customer name"
                    size="small"
                    fullWidth
                    value={form.delivery_address.name}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      delivery_address: { ...prev.delivery_address, name: e.target.value },
                    }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone"
                    size="small"
                    fullWidth
                    value={form.delivery_address.phone}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      delivery_address: { ...prev.delivery_address, phone: e.target.value },
                    }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    size="small"
                    fullWidth
                    value={form.delivery_address.email}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      delivery_address: { ...prev.delivery_address, email: e.target.value },
                    }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address line 1"
                    size="small"
                    fullWidth
                    value={form.delivery_address.address1}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      delivery_address: { ...prev.delivery_address, address1: e.target.value },
                    }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address line 2"
                    size="small"
                    fullWidth
                    value={form.delivery_address.address2}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      delivery_address: { ...prev.delivery_address, address2: e.target.value },
                    }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="City"
                    size="small"
                    fullWidth
                    value={form.delivery_address.city}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      delivery_address: { ...prev.delivery_address, city: e.target.value },
                    }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="State"
                    size="small"
                    fullWidth
                    value={form.delivery_address.state}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      delivery_address: { ...prev.delivery_address, state: e.target.value },
                    }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="PIN code"
                    size="small"
                    fullWidth
                    value={form.delivery_address.pincode}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      delivery_address: { ...prev.delivery_address, pincode: e.target.value },
                    }))}
                    inputProps={{ maxLength: 6 }}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard title="Notes" subtitle="Optional instructions or terms for the customer">
              <TextField
                placeholder="Add any notes, delivery terms, or special instructions…"
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                fullWidth
                multiline
                minRows={4}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#fafbfc",
                    fontSize: "0.875rem",
                  },
                }}
              />
            </SectionCard>
          </Box>

          {/* Sidebar */}
          <Box sx={{ minWidth: 0, position: { md: "sticky" }, top: 80, alignSelf: "start" }}>
              <Paper
                elevation={0}
                sx={{
                  mb: 2.5,
                  border: `1px solid ${LINE}`,
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "#fff",
                }}
              >
                <Box sx={{ height: 3, background: `linear-gradient(90deg, ${PURPLE} 0%, ${PURPLE_DARK} 100%)` }} />
                <Box sx={{ p: 2.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} color={INK} sx={{ mb: 2 }}>
                    Summary
                  </Typography>

                  <SummaryLine
                    label={`Subtotal (${totals.lineItems.length} items)`}
                    value={formatMoney(totals.subtotalMinor)}
                  />
                  {totals.discountMinor > 0 && (
                    <SummaryLine
                      label="Discount"
                      value={`-${formatMoney(totals.discountMinor)}`}
                      negative
                    />
                  )}
                  {totals.chargesMinor > 0 && (
                    <SummaryLine label="Charges" value={formatMoney(totals.chargesMinor)} />
                  )}
                  {totals.taxMinor > 0 && (
                    <SummaryLine label="Tax" value={formatMoney(totals.taxMinor)} />
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      px: 2,
                      py: 1.5,
                      mx: -0.5,
                      borderRadius: 1.5,
                      bgcolor: PURPLE_DARK,
                      color: "#fff",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      Grand Total
                    </Typography>
                    <Typography variant="h6" fontWeight={800}>
                      {formatMoney(totals.totalMinor)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <SectionCard title="Adjustments" subtitle="Discounts, charges & extra tax">
                {/* Discount */}
                <Box sx={{ mb: 2 }}>
                  <AdjustmentRowHeader
                    title="Discount"
                    actionLabel={discountEnabled ? "Remove" : "+ Add"}
                    onAction={() => setDiscountEnabled((v) => !v)}
                  />
                  {discountEnabled && (
                    <AdjustmentBox>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1.25,
                          alignItems: "start",
                        }}
                      >
                        <FormControl fullWidth size="small" sx={adjustmentFieldSx}>
                          <InputLabel>Type</InputLabel>
                          <Select
                            label="Type"
                            value={discountType}
                            onChange={(e) => setDiscountType(e.target.value)}
                          >
                            <MenuItem value="fixed">Fixed</MenuItem>
                            <MenuItem value="rate">Rate %</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          fullWidth
                          label="Value"
                          type="number"
                          value={discountValue}
                          onChange={(e) => setDiscountValue(e.target.value)}
                          sx={adjustmentFieldSx}
                        />
                      </Box>
                    </AdjustmentBox>
                  )}
                </Box>

                {/* Charges */}
                <Box sx={{ mb: 2 }}>
                  <AdjustmentRowHeader
                    title="Additional Charges"
                    actionLabel="+ Add"
                    onAction={() => setForm((prev) => ({
                      ...prev,
                      adjustments: {
                        ...prev.adjustments,
                        charges: [...(prev.adjustments.charges || []), emptyCharge()],
                      },
                    }))}
                  />
                  {(form.adjustments.charges || []).length === 0 && (
                    <Typography variant="caption" color={MUTED}>No extra charges</Typography>
                  )}
                  {(form.adjustments.charges || []).map((charge, index) => (
                    <AdjustmentBox key={index} sx={{ mb: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Title"
                        value={charge.title}
                        onChange={(e) => updateCharge(index, { title: e.target.value })}
                        sx={{ ...adjustmentFieldSx, mb: 1.25 }}
                      />
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 36px",
                          gap: 1.25,
                          alignItems: "end",
                        }}
                      >
                        <FormControl fullWidth size="small" sx={adjustmentFieldSx}>
                          <InputLabel>Type</InputLabel>
                          <Select
                            label="Type"
                            value={charge.type || "fixed"}
                            onChange={(e) => updateCharge(index, { type: e.target.value })}
                          >
                            <MenuItem value="fixed">Fixed</MenuItem>
                            <MenuItem value="rate">Rate %</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          fullWidth
                          label="Amount"
                          type="number"
                          value={charge.value}
                          onChange={(e) => updateCharge(index, { value: e.target.value })}
                          sx={adjustmentFieldSx}
                        />
                        <DeleteRowButton
                          onClick={() => setForm((prev) => ({
                            ...prev,
                            adjustments: {
                              ...prev.adjustments,
                              charges: prev.adjustments.charges.filter((_, i) => i !== index),
                            },
                          }))}
                        />
                      </Box>
                    </AdjustmentBox>
                  ))}
                </Box>

                {/* Tax */}
                <Box>
                  <AdjustmentRowHeader
                    title="Extra Tax"
                    actionLabel="+ Add"
                    onAction={() => setForm((prev) => ({
                      ...prev,
                      adjustments: {
                        ...prev.adjustments,
                        taxes: [...(prev.adjustments.taxes || []), emptyTax()],
                      },
                    }))}
                  />
                  {(form.adjustments.taxes || []).length === 0 && (
                    <Typography variant="caption" color={MUTED}>
                      Line-item GST is applied per product
                    </Typography>
                  )}
                  {(form.adjustments.taxes || []).map((tax, index) => (
                    <AdjustmentBox key={index} sx={{ mb: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Tax name"
                        value={tax.title}
                        onChange={(e) => updateTax(index, { title: e.target.value })}
                        sx={{ ...adjustmentFieldSx, mb: 1.25 }}
                      />
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 36px",
                          gap: 1.25,
                          alignItems: "end",
                        }}
                      >
                        <TextField
                          size="small"
                          fullWidth
                          label="Rate %"
                          type="number"
                          value={tax.rate_percent}
                          onChange={(e) => updateTax(index, { rate_percent: e.target.value })}
                          sx={adjustmentFieldSx}
                        />
                        <DeleteRowButton
                          onClick={() => setForm((prev) => ({
                            ...prev,
                            adjustments: {
                              ...prev.adjustments,
                              taxes: prev.adjustments.taxes.filter((_, i) => i !== index),
                            },
                          }))}
                        />
                      </Box>
                    </AdjustmentBox>
                  ))}
                </Box>
              </SectionCard>
          </Box>
        </Box>
      </Box>

      <CatalogProductPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handleProductsConfirm}
        initialItems={form.line_items}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
